import React, { useMemo, memo } from 'react';
import { useLoggedInUser, useFeatureFlags } from '@tcc/shared-data-lib';
import { importFromS3 } from '../../utils/importFromS3';
import { Operation } from '../../hooks/useOperations/types';
import { useImpersonatedOperation } from '../../hooks/useImpersonatedOperation';
import { useUserLoggedInConfigs, useFeatureFlagsConfigs } from '../../utils/sharedData';

interface IProps {
  operations: Operation[],
  operationsSelected: string[]
}

const S3Plugin = React.lazy(() => importFromS3('variable-rate'));

export const VariableRatePluginContainer: React.FC<IProps> = memo(({ operations, operationsSelected }: IProps) => {
  const { user } = useLoggedInUser(useUserLoggedInConfigs);
  const { featureFlags } = useFeatureFlags(useFeatureFlagsConfigs(user));
  const useModuleFederation = featureFlags?.['web.variableRate.module-federation'];

  const VariableRatePlugin = useMemo(() => {
    if (useModuleFederation) {
      return React.lazy(() => import('variableRate/VariableRate'));
    }
    return S3Plugin;
  }, [useModuleFederation]);

  const { isLoading, operation: impersonatedOperation } = useImpersonatedOperation();
  const selectedOperations = useMemo(() => impersonatedOperation 
    ? [impersonatedOperation.operationId] 
    : operationsSelected, 
  [impersonatedOperation, operationsSelected]);

  if (isLoading || !selectedOperations.length) return null;

  return (
    <VariableRatePlugin
      operations={operations}
      operationsSelected={selectedOperations}
    />
  );
}, (prevProps, nextProps) => prevProps.operationsSelected === nextProps.operationsSelected);